// @flow
import * as React from 'react';
import i18n from 'app/common/lib/i18n';
import IndexManager from 'app/common/react/components/common/SectionList/IndexManager';
import PickerSearchField from 'app/common/react/components/RepinPicker/PickerSearchField/PickerSearchField';
import hoverable, { type hoverHandlersType } from 'app/common/lib/react/hoverable';
import SectionList, {
  type Item,
  type Section,
  type ItemRenderProps,
} from 'app/common/react/components/common/SectionList/SectionList';
import { Box, Button, Icon, Text, Touchable } from 'gestalt';

type Props = {|
  createBtnDisabled?: boolean,
  dropdownValue: string,
  fetchMore?: () => void,
  hoverHandlers: hoverHandlersType,
  isHovering: boolean,
  onCreate: ?() => void,
  onSearchQueryUpdate: (query: string) => void,
  onSelect: (item: Item) => void,
  renderRow: (item: ItemRenderProps) => ?React.Element<any>,
  searchQuery: string,
  sections: Array<Section>,
|};

type State = {|
  showFlyout: boolean,
|};

export class PickerFlyout extends React.Component<Props, State> {
  state: State = {
    showFlyout: false,
  };

  componentDidMount() {
    document.addEventListener('click', this.handleHideFlyout, {
      capture: true,
    });
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleHideFlyout, {
      capture: true,
    });
  }

  setContainerRef = (ref: ?HTMLElement) => {
    this.containerRef = ref;
  };

  handleCreate = () => {
    this.hideFlyout();
    const { onCreate } = this.props;
    if (onCreate) {
      onCreate();
    }
  };

  handleSelectItem = (item: Item) => {
    this.hideFlyout();
    const { onSelect, onCreate } = this.props;
    if (!item) {
      if (onCreate) {
        onCreate();
      }
    } else {
      onSelect(item);
    }
  };

  handleDropdownTouch = () => {
    this.setState((prevState) => ({ showFlyout: !prevState.showFlyout }));
  };

  handleHideFlyout = (event: Event) => {
    if (
      this.containerRef &&
      event.target instanceof HTMLElement &&
      !this.containerRef.contains(event.target)
    ) {
      this.hideFlyout();
    }
  };

  hideFlyout = () => {
    const {
      hoverHandlers: { onMouseLeave },
    } = this.props;
    onMouseLeave();
    this.setState({ showFlyout: false });
  };

  containerRef: ?HTMLElement;

  indexManager: IndexManager;

  render() {
    const {
      dropdownValue,
      fetchMore,
      hoverHandlers: { onMouseOver, onMouseLeave },
      isHovering,
      onCreate,
      onSearchQueryUpdate,
      renderRow,
      searchQuery,
      sections,
      createBtnDisabled,
    } = this.props;
    const { showFlyout } = this.state;

    this.indexManager = new IndexManager(sections.map((section) => section.items));
    this.indexManager.setSelectItem(this.handleSelectItem);

    const shouldDisableCreateBtn = !searchQuery.trim() || !!createBtnDisabled;
    return (
      <div ref={this.setContainerRef}>
        <Touchable
          onMouseEnter={onMouseOver}
          onMouseLeave={onMouseLeave}
          onTouch={this.handleDropdownTouch}
          fullWidth={false}
          shape="rounded"
        >
          <Box
            alignItems="center"
            color={isHovering ? 'lightWash' : 'lightGray'}
            direction="row"
            display="flex"
            flex="grow"
            paddingX={4}
            paddingY={3}
            shape="rounded"
          >
            <Box display="flex" flex="grow">
              <Text weight="bold">{dropdownValue}</Text>
            </Box>
            <Icon icon="arrow-down" size={14} color="darkGray" accessibilityLabel="" />
          </Box>
        </Touchable>
        {showFlyout && (
          <Box position="relative">
            <Box
              shape="rounded"
              color="white"
              display="flex"
              direction="column"
              overflow="hidden"
              position="absolute"
              width="100%"
              dangerouslySetInlineStyle={{
                __style: {
                  border: '1px solid #efefef',
                  zIndex: 1,
                },
              }}
            >
              <Box>
                <Box display="flex" padding={2} marginLeft={-1} marginRight={-1}>
                  <Box display="flex" flex="grow" paddingX={1}>
                    <PickerSearchField
                      hideIcon
                      indexManager={this.indexManager}
                      onChange={onSearchQueryUpdate}
                      value={searchQuery}
                    />
                  </Box>
                  {onCreate && (
                    <Box paddingX={1}>
                      <Button
                        disabled={shouldDisableCreateBtn}
                        inline
                        onClick={this.handleCreate}
                        text={i18n._('Create', 'board or section dropdown create button')}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              <SectionList
                fetchMore={fetchMore}
                indexManager={this.indexManager}
                maxHeight={240}
                onSelectItem={this.handleSelectItem}
                renderItem={renderRow}
                sections={sections}
              />
            </Box>
          </Box>
        )}
      </div>
    );
  }
}

export default hoverable(PickerFlyout);
